import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A fade slideshow component for product media galleries.
 * Provides smooth fade transitions between images with auto-advance and touch/click navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides - The slide elements
 * @property {HTMLElement} container - The container element
 *
 * @extends Component<Refs>
 */
export class FadeSlideshow extends Component {
  static get observedAttributes() {
    return ['interval', 'autoplay'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === 'interval') {
      this.#restartAutoplay();
    } else if (name === 'autoplay') {
      if (newValue === 'false' || newValue === null) {
        this.#stopAutoplay();
      } else {
        this.#startAutoplay();
      }
    }
  }

  #currentIndex = 0;
  #autoplayTimer = null;
  #touchStartX = null;
  #touchStartY = null;
  #isTransitioning = false;

  /**
   * Initializes the slideshow
   */
  #initialize() {
    const { slides } = this.refs;
    if (!slides || slides.length === 0) return;

    // Set initial state
    slides.forEach((slide, index) => {
      slide.style.opacity = index === 0 ? '1' : '0';
      slide.style.pointerEvents = index === 0 ? 'auto' : 'none';
    });

    // Add event listeners
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: false });

    // Start autoplay if enabled
    if (this.getAttribute('autoplay') !== 'false') {
      this.#startAutoplay();
    }
  }

  /**
   * Cleans up timers and event listeners
   */
  #cleanup() {
    this.#stopAutoplay();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchend', this.#handleTouchEnd);
  }

  /**
   * Handles click events for navigation (left/right sides)
   */
  #handleClick = (event) => {
    if (this.#isTransitioning) return;

    const rect = this.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width;
    const threshold = width / 2;

    if (clickX < threshold) {
      this.previous();
    } else {
      this.next();
    }
  };

  /**
   * Handles touch start for swipe detection
   */
  #handleTouchStart = (event) => {
    if (this.#isTransitioning) return;
    const touch = event.touches[0];
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
  };

  /**
   * Handles touch end for swipe detection
   */
  #handleTouchEnd = (event) => {
    if (this.#isTransitioning || this.#touchStartX === null) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.#touchStartX;
    const deltaY = touch.clientY - this.#touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Only handle horizontal swipes (ignore if vertical movement is greater)
    if (absDeltaX > absDeltaY && absDeltaX > 50) {
      event.preventDefault();
      if (deltaX > 0) {
        this.previous();
      } else {
        this.next();
      }
    }

    this.#touchStartX = null;
    this.#touchStartY = null;
  };

  /**
   * Starts autoplay
   */
  #startAutoplay() {
    this.#stopAutoplay();
    const interval = parseInt(this.getAttribute('interval') || '4000', 10);
    if (interval > 0 && !prefersReducedMotion()) {
      this.#autoplayTimer = setInterval(() => {
        this.next();
      }, interval);
    }
  }

  /**
   * Stops autoplay
   */
  #stopAutoplay() {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  /**
   * Restarts autoplay with new interval
   */
  #restartAutoplay() {
    if (this.getAttribute('autoplay') !== 'false') {
      this.#startAutoplay();
    }
  }

  /**
   * Transitions to a specific slide
   */
  #goToSlide(index) {
    const { slides } = this.refs;
    if (!slides || slides.length === 0 || this.#isTransitioning) return;

    const targetIndex = ((index % slides.length) + slides.length) % slides.length;
    if (targetIndex === this.#currentIndex) return;

    this.#isTransitioning = true;
    const currentSlide = slides[this.#currentIndex];
    const targetSlide = slides[targetIndex];

    // Use CSS transitions for smooth fade
    const duration = prefersReducedMotion() ? 0 : 800;
    const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';

    // Fade out current slide
    currentSlide.style.transition = `opacity ${duration}ms ${easing}`;
    currentSlide.style.opacity = '0';
    currentSlide.style.pointerEvents = 'none';

    // Fade in target slide
    targetSlide.style.transition = `opacity ${duration}ms ${easing}`;
    targetSlide.style.opacity = '1';
    targetSlide.style.pointerEvents = 'auto';

    this.#currentIndex = targetIndex;

    // Reset transition flag after animation
    setTimeout(() => {
      this.#isTransitioning = false;
      // Remove transition property to allow instant changes on next transition
      slides.forEach(slide => {
        slide.style.transition = '';
      });
    }, duration);
  }

  /**
   * Goes to the next slide
   */
  next() {
    this.#goToSlide(this.#currentIndex + 1);
    this.#restartAutoplay();
  }

  /**
   * Goes to the previous slide
   */
  previous() {
    this.#goToSlide(this.#currentIndex - 1);
    this.#restartAutoplay();
  }

  /**
   * Gets the current slide index
   */
  get currentIndex() {
    return this.#currentIndex;
  }
}

if (!customElements.get('fade-slideshow')) {
  customElements.define('fade-slideshow', FadeSlideshow);
}

